/**
 * Comparing a calendar day to a moment in time.
 *
 * An event's `date` is a **day**, not an instant — `2026-10-23`, not
 * `2026-10-23T00:00:00Z`. Comparing it to `now` with `<` therefore asks a
 * question with no answer: a day is not before or after a moment, it *contains*
 * moments. Every place that tried anyway picked the day's UTC midnight as a
 * stand-in and got "this event is in the past" from its own small hours.
 *
 * These helpers make the comparison say what it means: the day the moment falls
 * on, compared to the day in question. `docs/event-scheduling.md` is about this
 * distinction throughout.
 *
 * **The anchor is UTC, and that is temporary.** The day a moment falls on
 * depends on where you are standing, and the right answer is the venue's clock —
 * which is why `Venue.timezone` now exists. Until the venue window slices land
 * and every caller has a venue in hand, UTC is the anchor, exactly as it was
 * before; these helpers change *what* is compared, not the zone it is compared
 * in. When that changes, it changes here.
 */
import { calendarDayOf, type CalendarDay } from '../validators/temporal.validator.js'

/** The `yyyy-MM-dd` a moment falls on, in UTC. */
export function utcDay(at: Date): CalendarDay {
  return calendarDayOf(at)
}

/**
 * The UTC midnight that starts the day a moment falls on.
 *
 * This is the value to compare a `date` column against: Postgres reads a
 * timestamp bound against a `date` column as a day, so passing a mid-afternoon
 * `now` compares against *today* while reading as though it compared against
 * this afternoon. Passing the day's start makes the two agree.
 */
export function startOfUtcDay(at: Date): Date {
  return new Date(`${utcDay(at)}T00:00:00.000Z`)
}

/**
 * A `yyyy-MM-dd` as the UTC midnight starting it.
 *
 * `new Date('2026-10-23T00:00:00.000')` — the same string without the `Z` — is
 * midnight *in the server's zone*, which lands on the 23rd in London and the
 * 23rd at 07:00Z in California. Bound against a `date` column the second one
 * truncates to a different day, so a range filter built that way answered
 * differently depending on where the server was. Days are zoneless; parse them
 * that way.
 */
export function parseUtcDay(day: CalendarDay): Date {
  return new Date(`${day}T00:00:00.000Z`)
}

/**
 * True if `day` falls on a calendar day strictly before the one `at` falls on.
 *
 * The SQL twin of this is `{ date: { lt: startOfUtcDay(at) } }` — see
 * `frozenPhaseWhere` and `statusWhere`, which must agree with their JS
 * counterparts.
 */
export function dayIsBefore(day: Date, at: Date): boolean {
  return utcDay(day) < utcDay(at)
}
